<template>
  <PVDrawer
    :id="id"
    v-model:visible="model"
    :position="position"
    :modal="shouldShowBackdrop"
    :dismissable="isDismissable"
    :show-close-icon="shouldShowCloseIcon"
    :close-on-escape="shouldCloseOnEscape"
    :block-scroll="shouldBlockScroll"
    :base-z-index="baseZIndex"
    :auto-z-index="autoZIndex"
    :pt="passThroughProps"
    @show="emit('show')"
    @hide="emit('hide')"
    @after-show="emit('after-show')"
    @after-hide="emit('after-hide')"
    @before-hide="emit('before-hide')"
  >
    <template
      v-if="slots.header"
      #header="slotProps"
    >
      <slot
        name="header"
        v-bind="slotProps"
      />
    </template>

    <template
      v-if="slots.closebutton"
      #closebutton="slotProps"
    >
      <slot
        name="closebutton"
        v-bind="slotProps"
      />
    </template>

    <slot />

    <template
      v-if="slots.footer"
      #footer
    >
      <slot name="footer" />
    </template>
  </PVDrawer>
</template>

<script lang="ts">
export default {
  name: 'StandardDrawer',
  compatConfig: { MODE: 3 },
};
</script>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import type { DrawerPassThroughAttributes, DrawerProps } from 'primevue/drawer';
import { DEFAULT_SIDEBAR_Z_INDEX } from '@/constants/bootstrap';

const model = defineModel<boolean>({ required: true });

const props = withDefaults(
  defineProps<{
    id?: string;
    /** The position of the drawer */
    position?: DrawerProps['position'];
    headerClass?: string;
    contentClass?: string;
    /**
     * When true, shows a darkened modal mask behind the drawer.
     */
    shouldShowBackdrop?: boolean;
    /** Whether clicking outside closes the panel */
    isDismissable?: boolean;
    /** Whether to display a close icon inside the panel */
    shouldShowCloseIcon?: boolean;
    /** Whether pressing the escape key closes the drawer */
    shouldCloseOnEscape?: boolean;
    /** Whether background scroll should be blocked when drawer is visible */
    shouldBlockScroll?: boolean;
    baseZIndex?: number;
    autoZIndex?: boolean;
    /**
     * Offset from the top of the viewport (e.g. below the app navbar).
     * Applied to the drawer mask, which wraps the panel (PrimeVue nests root inside mask).
     */
    offsetTop?: string;
    offsetLeft?: string;
    /**
     * CSS width for the drawer panel when position is left/right.
     */
    width?: string;
  }>(),
  {
    position: 'left',
    shouldShowBackdrop: true,
    isDismissable: true,
    shouldShowCloseIcon: true,
    shouldCloseOnEscape: true,
    shouldBlockScroll: false,
    baseZIndex: DEFAULT_SIDEBAR_Z_INDEX,
    autoZIndex: true,
    width: '25%',
  },
);

const emit = defineEmits<{
  (e: 'show'): void;
  (e: 'hide'): void;
  (e: 'after-show'): void;
  (e: 'after-hide'): void;
  (e: 'before-hide'): void;
}>();

const slots = useSlots();

/**
 * A left drawer offset from the left edge would otherwise overhang the window and clip without
 * a scrollbar. Cap its width at the space actually available.
 */
const drawerWidth = computed((): string => {
  if (props.position !== 'left') return props.width;

  return `min(${props.width}, calc(100vw - ${props.offsetLeft ?? '0px'}))`;
});

const passThroughProps = computed((): DrawerPassThroughAttributes => {
  return {
    root: {
      class: ['standard-drawer', props.position === 'left' || props.position === 'right' ? 'border-right' : ''],
      style: {
        display: 'flex',
        flexDirection: 'column',
        // Side drawers shortened by the navbar offset would otherwise be vertically centered
        // by the mask, and their bottom edge would extend below the fold. Pin those to the
        // top. Bottom drawers must keep the mask's flex-end alignment.
        ...(props.offsetTop === undefined || (props.position !== 'left' && props.position !== 'right')
          ? {}
          : {
              top: props.offsetTop,
              height: `calc(100% - ${props.offsetTop})`,
              alignSelf: 'flex-start',
            }),
        width: props.position === 'left' || props.position === 'right' ? drawerWidth.value : undefined,
        ...(props.offsetLeft === undefined ? {} : { left: props.offsetLeft }),
        // PrimeVue's drawer sets border-style without a width, so the bottom side keeps the
        // 3px "medium" default, lifting the grid (and its scroll shadow) off the drawer edge.
        borderTop: 'none',
        borderLeft: 'none',
        borderBottom: 'none',
      },
    },
    mask: {
      class: 'standard-drawer-mask',
      // PrimeVue only writes baseZIndex when autoZIndex is on, and that path adds the modal
      // counter, which lifts a persistent drawer above StandardTooltip (1100).
      ...(props.autoZIndex ? {} : { style: { zIndex: props.baseZIndex } }),
    },
    header: {
      class: ['standard-drawer-header', props.headerClass].filter(Boolean).join(' '),
      // PrimeVue always mounts the header. With no slot and no close icon it is only padding.
      ...(!slots.header && !props.shouldShowCloseIcon ? { style: { display: 'none' } } : {}),
    },
    content: {
      class: ['standard-drawer-content p-0', props.contentClass].filter(Boolean).join(' '),
    },
    footer: {
      class: 'standard-drawer-footer p-0',
    },
  };
});
</script>

<style lang="scss">
.standard-drawer.p-drawer {
  display: flex;
  flex-direction: column;
}

.standard-drawer-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
}

.standard-drawer-header {
  flex-shrink: 0;
}

.standard-drawer-footer {
  flex-shrink: 0;
}
</style>
